import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FormDateField, FormCheckbox, FormikWrapper } from '@hvcgroep/design-system';
import { RegistrationDeskContext, RegistrationDeskEventHandler } from '../../state-machine';
import AddressSelectionInfo from '../../../../shared/containers/AddressSelection/Info';
import { EamData } from '../../types/orchestrator';
import useAddressFields, { AddressSelectionFields } from '../../../../shared/containers/AddressSelection/Fields';
import JourneyActionButtons from '../../components/JourneyActionButtons';
import { RegistrationDeskStepComponent } from '../../types/multistep-form';
import gtm from '../../../../shared/helpers/gtm';
import { APP_ID } from '../../../../shared/config/constants';
import trackValidationError from '../../../../shared/helpers/trackValidationError';

interface Props {
  onAddressChange: RegistrationDeskEventHandler<'reset.changeDeliveryAddress'>
  context: Pick<RegistrationDeskContext, 'eamData' | 'startDate' | 'billingAddress'>
}

const maxStartDate = new Date();
maxStartDate.setDate(maxStartDate.getDate() + 90);
maxStartDate.setHours(23, 59, 59, 999);

const minStartDate = new Date();
minStartDate.setDate(minStartDate.getDate() - 90);
minStartDate.setHours(23, 59, 59, 999);

const DeliveryDetailsForm: RegistrationDeskStepComponent<'proceed.saveDeliveryDetails', Props> = (props) => {
  const {
    onProceed,
    onAddressChange,
    onGoBack,
    context,
  } = props;

  const {
    submit: onAddressSubmit,
    initialValues: initialAddressValues,
    validationScheme: addressValidationScheme,
    props: addressFieldProps,
  } = useAddressFields({
    defaultAddress: context.billingAddress,
  });

  const registrationType = context.registrationType!;

  const { t } = useTranslation();

  const minStartDateValidation = new Date(minStartDate);
  minStartDateValidation.setHours(0, 0, 0, 0);

  const maxStartDateValidation = new Date(maxStartDate);
  maxStartDateValidation.setHours(0, 0, 0, 0);

  return (
    <div>
      <AddressSelectionInfo
        address={context.eamData as EamData}
        onAddressReset={() => onAddressChange({})}
        addressMapper={{
          house_number: 'Housenumber',
          house_number_extension: 'HousenumberExt',
          city: 'City',
          postal_code: 'PostalCode',
          street: 'Street',
        }}
      />
      <FormikWrapper
        onSubmit={async (values, helpers) => {
          if (values.isDifferentBillingAddress) {
            const billingAddress = await onAddressSubmit(values, helpers);

            if (!billingAddress) {
              return;
            }

            onProceed({
              startDate: new Date(values.startDate),
              billingAddress,
            });

            return;
          }

          onProceed({
            startDate: new Date(values.startDate),
            billingAddress: null,
          });
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          startDate: Yup.date().required(() => {
            return registrationType === 'moving'
              ? t('registration_desk.screens.delivery_details_form.start_date.error.required.moving')
              : t('registration_desk.screens.delivery_details_form.start_date.error.required.renovation');
          })
            .min(minStartDateValidation, t('registration_desk.screens.delivery_details_form.start_date.error.format'))
            .max(maxStartDateValidation, t('registration_desk.screens.delivery_details_form.start_date.error.format')),
          isBillingAddressDifferent: Yup.boolean(),
          ...addressValidationScheme,
          // Override the postal_code and house_number validations since they are dependent on whether or not
          // the billing address is different from the delivery address; these fields are namely always required
          // in the useAddressFields hook.
          postal_code: Yup.string()
            .when('isDifferentBillingAddress', {
              is: true,
              then: Yup.string().required(t('registration_desk.screens.delivery_details_form.is_diffirent_billing_address.postal_code.error.required')).matches(/^ *[1-9][0-9]{3} *(?!sa|sd|ss)[a-z]{2}$/i, t('form.error.invalid_postal_code')),
            }),
          house_number: Yup.string()
            .when('isDifferentBillingAddress', {
              is: true,
              then: Yup.string().required(t('registration_desk.screens.delivery_details_form.is_diffirent_billing_address.house_number.error.required')),
            }),
        })}
        initialValues={{
          startDate: context.startDate ? context.startDate.toISOString().split('T')[0] : '',
          isDifferentBillingAddress: !!context.billingAddress,
          postal_code: context.billingAddress?.postcode ? String(context.billingAddress?.postcode) : initialAddressValues.postal_code,
          house_number: context.billingAddress?.huisnummer ? String(context.billingAddress?.huisnummer) : initialAddressValues.house_number,
          selection: context.billingAddress?.nummeraanduiding_id ?? initialAddressValues.selection,
        }}
        onValidationError={(errors) => {
          trackValidationError(String(APP_ID), 'DeliveryDetailsForm', errors);
        }}
      >
        {({
          values,
        }) => {
          return (
            <Form noValidate>
              <FormCheckbox
                name="isDifferentBillingAddress"
                label={t('registration_desk.screens.delivery_details_form.is_diffirent_billing_address.label')}
                visualValidation={false}
                isLiveValidationDisabled
                onChange={(e) => {
                  gtm.trackUserEvent(String(APP_ID), 'Delivery address equals billing address', e.target.checked ? 'yes' : 'no');
                }}
              />
              {values.isDifferentBillingAddress && (
                <AddressSelectionFields
                  isLiveValidationDisabled
                  {...addressFieldProps}
                />
              )}
              <FormDateField
                label={t('registration_desk.screens.delivery_details_form.start_date.label')}
                description={t(`registration_desk.screens.delivery_details_form.start_date.description.${registrationType}`)}
                isLiveValidationDisabled
                name="startDate"
                min={minStartDate}
                max={maxStartDate}
              />
              <JourneyActionButtons
                onGoBack={() => {
                  if (!onGoBack) return;

                  if (values.isDifferentBillingAddress) {
                    onAddressSubmit(values).then((billingAddress) => {
                      if (!billingAddress) {
                        onGoBack({
                          startDate: values.startDate ? new Date(values.startDate) : null as unknown as Date,
                          billingAddress: null,
                        });

                        return;
                      }

                      onGoBack({
                        startDate: values.startDate ? new Date(values.startDate) : null as unknown as Date,
                        billingAddress,
                      });
                    });

                    return;
                  }

                  onGoBack({
                    startDate: values.startDate ? new Date(values.startDate) : null as unknown as Date,
                    billingAddress: null,
                  });
                }}
              />
            </Form>
          );
        }}
      </FormikWrapper>
    </div>
  );
};

export default DeliveryDetailsForm;

